import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { lovable } from '@/integrations/lovable/index';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Logo } from '@/components/Logo';

type Tab = 'login' | 'signup';

export default function Auth() {
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>('login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Detect OAuth callback errors in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    const error = params.get('error') || hashParams.get('error');
    const errorDescription = params.get('error_description') || hashParams.get('error_description');

    if (error) {
      let friendlyMessage = 'Something went wrong. Please try again.';
      if (
        error === 'access_denied' ||
        errorDescription?.toLowerCase().includes('expired') ||
        errorDescription?.toLowerCase().includes('invalid state')
      ) {
        friendlyMessage = 'Your sign-in session expired. Please try again.';
      } else if (errorDescription) {
        friendlyMessage = errorDescription;
      }
      setOauthError(friendlyMessage);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    setOauthError(null);
    setFormError(null);
    setGoogleLoading(true);

    const result = await lovable.auth.signInWithOAuth('google', {
      redirect_uri: window.location.origin,
    });

    if (result.error) {
      toast({
        title: 'Unable to continue',
        description: result.error.message,
        variant: 'destructive',
      });
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setFormError(error.message);
    }
    setLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      setFormError(error.message);
    } else if (data.session) {
      // Auto-confirmed, session exists — redirect handled by AuthRoute
    } else {
      setSignupSuccess(true);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setResetSent(true);
    }
    setResetLoading(false);
  };

  // Forgot password view
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center space-y-3">
            <Logo size="lg" className="mx-auto text-foreground" />
            <p className="text-muted-foreground text-base">Reset your password</p>
          </div>
          {resetSent ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">Check your email for a password reset link.</p>
              <Button variant="ghost" onClick={() => { setShowForgotPassword(false); setResetSent(false); }}>
                Back to login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <Input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Email"
                className="h-11 bg-muted/30 border-border/50 rounded-lg text-base"
                required
              />
              <Button type="submit" className="w-full h-11" disabled={resetLoading}>
                {resetLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send reset link
              </Button>
              <Button variant="ghost" type="button" className="w-full" onClick={() => setShowForgotPassword(false)}>
                Back to login
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Signup success view
  if (signupSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm space-y-8 text-center">
          <Logo size="lg" className="mx-auto text-foreground" />
          <div className="space-y-2">
            <h2 className="text-lg font-medium text-foreground">Check your email</h2>
            <p className="text-sm text-muted-foreground">We sent a verification link to <strong>{email}</strong>. Click it to activate your account.</p>
          </div>
          <Button variant="ghost" onClick={() => { setSignupSuccess(false); setTab('login'); }}>
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
      <Link
        to="/"
        className="absolute top-6 left-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Link>

      <div className="w-full max-w-sm space-y-10">
        {/* Brand */}
        <div className="text-center space-y-3">
          <Logo size="lg" className="mx-auto text-foreground" />
          <p className="text-muted-foreground text-base">A quiet place for your thoughts.</p>
        </div>

        {/* OAuth Error Recovery */}
        {oauthError && (
          <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Sign-in interrupted</p>
                <p className="text-sm text-muted-foreground">{oauthError}</p>
              </div>
            </div>
            <Button onClick={handleGoogleSignIn} variant="outline" size="sm" className="w-full gap-2" disabled={googleLoading}>
              {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Try again
            </Button>
          </div>
        )}

        {/* Google */}
        <Button
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          className="w-full h-12 text-base font-medium bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shadow-sm"
        >
          {googleLoading ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : (
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          )}
          Continue with Google
        </Button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border/50" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-background px-4 text-sm text-muted-foreground/70">Or continue with email</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="space-y-6">
          <div className="flex rounded-lg bg-muted/40 p-1">
            <button
              type="button"
              onClick={() => { setTab('login'); setFormError(null); }}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'login' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Log in
            </button>
            <button
              type="button"
              onClick={() => { setTab('signup'); setFormError(null); }}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'signup' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Sign up
            </button>
          </div>

          {/* Form error */}
          {formError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {tab === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="h-11 bg-muted/30 border-border/50 rounded-lg text-base placeholder:text-muted-foreground/50"
                required
              />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="h-11 bg-muted/30 border-border/50 rounded-lg text-base placeholder:text-muted-foreground/50"
                required
              />
              <Button
                type="submit"
                variant="ghost"
                className="w-full h-11 text-base font-normal text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Continue
              </Button>
              <button
                type="button"
                onClick={() => { setShowForgotPassword(true); setResetEmail(email); }}
                className="block w-full text-center text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
              >
                Forgot password?
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="h-11 bg-muted/30 border-border/50 rounded-lg text-base placeholder:text-muted-foreground/50"
                required
              />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="h-11 bg-muted/30 border-border/50 rounded-lg text-base placeholder:text-muted-foreground/50"
                minLength={6}
                required
              />
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className="h-11 bg-muted/30 border-border/50 rounded-lg text-base placeholder:text-muted-foreground/50"
                minLength={6}
                required
              />
              <Button
                type="submit"
                variant="ghost"
                className="w-full h-11 text-base font-normal text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create account
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
